<template>
  <div
    class="member-dropdown-wrap"
    @mousedown.stop
    @keydown.esc.stop="closeMembersDropdownListHandler"
  >
    <MembersDropdownList
      ref="membersDropdownList"
      :style="position"
      class="members-dropdown"
      :members="members"
      :child-card-item="childCardItem"
      :single-select="singleSelect"
      :show-shortcut-pill="showShortcutPill"
      :is-quick-card-modal="isQuickCardModal"
      :resource-type="resourceType"
      :entity="entity"
      :entity-type-prop="entityType"
      @startDragging="closeMembersDropdownListHandler"
      @closeDropdown="closeMembersDropdownListHandler"
      @memberClick="memberClick"
      @clickOutside="clickOutsideHandler"
      @selected-item="selectedItemHandler"
    />
  </div>
</template>

<script>
import MembersDropdownList from '@/components/widgets/MembersDropdownList';
import membersDropdown from '@/mixins/membersDropdown';
import { ResourceType } from '@/types/resources';

export default {
  name: 'MemberDropdownWrap',
  components: {
    MembersDropdownList,
  },
  mixins: [membersDropdown],
  props: {
    entity: {
      type: Object,
      required: true,
    },
    projectMembers: {
      type: Array,
      required: false,
      default: () => [],
    },
    shown: {
      type: String,
      required: false,
      default: '',
    },
    childCardItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    singleSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    showShortcutPill: {
      type: Boolean,
      required: false,
      default: false,
    },

    quickCard: {
      type: Boolean,
      required: false,
      default: false,
    },

    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },

    isQuickCardModal: {
      type: Boolean,
      required: false,
      default: false,
    },

    resourceType: {
      type: String,
      required: false,
      default: '',
    },

    // added as a way to ignore propagation of clickOutside event
    // which is handled by tippy itself
    inTippy: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['closeDropdown', 'memberClick'],
  data() {
    return {
      mobilePosition: {
        inset: '10% 0 auto 0',
        margin: 'auto',
      },
    };
  },
  computed: {
    position() {
      if (this.quickCard || this.isTemplate) return '';
      if (this.isMobile && this.isBoard) {
        return this.mobilePosition;
      }

      return Object.keys(this.$store.getters.getHeaderMemberDropdownPosition).length
        ? this.$store.getters.getHeaderMemberDropdownPosition
        : {};
    },
    sidebarExpanded() {
      return this.$store.getters.sidebarExpanded;
    },
    members() {
      return this.resourceType === 'project'
        ? this.projectMembers
        : this.entity.members || /* some epics dont have members prop */ [];
    },
    currentCard() {
      return this.resourceType === ResourceType.Card ? this.entity : {};
    },
    isBoard() {
      return this.$route.name === 'board';
    },
    addMembersTarget() {
      return this.$store.getters.getAddMembersTarget || {};
    },
    isDetailsSidebarOwner() {
      const dropdownId = this.$store.getters.getCurrentCardIdMemberDropdown;

      const isDetailsSidebar =
        dropdownId.startsWith(this.$cons.cardDetailsSidebar) ||
        dropdownId.startsWith(this.$cons.epicDetailsSidebar);
      const isOwnerDropdown = dropdownId.includes(this.$cons.owner);

      return isDetailsSidebar && isOwnerDropdown;
    },
  },
  watch: {
    shown() {
      this.$refs.membersDropdownList.scrollToTop();
    },
  },
  mounted() {
    this.setAddMembersTarget();
  },
  methods: {
    setAddMembersTarget() {
      const templateType = this.$constants.settingsTemplate.root;
      if (this.isTemplate && this.addMembersTarget.type !== templateType) {
        this.$store.dispatch('setAddMembersTarget', { type: templateType });
      }
    },
    selectedItemHandler(member) {
      if (this.isDetailsSidebarOwner) {
        const oldOwnerId = this.currentCard.owner_id;
        this.$store.dispatch('setCardOwnerId', {
          ownerId: member.id,
          cardId: this.currentCard.id,
        });
        this.$store
          .dispatch('updateCard', {
            cardId: this.currentCard.id,
            card: { owner_id: member.id },
            type: this.currentCard.type,
          })
          .catch(() => {
            this.$store.dispatch('setCardOwnerId', {
              ownerId: oldOwnerId,
              cardId: this.currentCard.id,
            });
          });
      }
      this.closeMembersDropdownList();
    },
    clickOutsideHandler() {
      if (this.inTippy) return;
      this.closeMembersDropdownListHandler();
    },
    closeMembersDropdownListHandler() {
      this.$emit('closeDropdown');
      this.closeMembersDropdownList();
      this.$refs.membersDropdownList.clearSearchInput();
    },
    memberClick(member) {
      this.$emit('memberClick', member);
    },
  },
};
</script>

<style lang="scss">
.member-dropdown-wrap {
  .members-dropdown {
    @apply shadow-md;

    .__panel {
      border-bottom-right-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    &:not(.tippy-content *) {
      position: absolute;
      z-index: 20;
    }
  }

  &.quick-card-members-dropdown,
  &.template-card-members-dropdown,
  &.space-members-dropdown {
    .members-dropdown {
      position: unset;
    }
  }
}
</style>
